<template>
  <div class="relative min-h-screen">
    <div class="page-bg pointer-events-none" aria-hidden="true">
      <img
        v-if="currentBg"
        :src="currentBg"
        class="bg-img"
        :class="{ 'visible': currentVisible }"
        alt=""
        decoding="async"
        loading="eager"
        @load="onCurrentImgLoad"
        @error="onCurrentImgError"
      />

      <img
        v-if="nextBg"
        :src="nextBg"
        class="bg-img"
        :class="{ 'visible': nextVisible }"
        alt=""
        decoding="async"
        loading="eager"
      />

      <div
        class="bg-placeholder"
        :class="{ 'hidden': placeholderHidden && hasAnyBg }"
        :style="placeholderStyle"
      ></div>

      <!-- overlay: background-color fixed, animate opacity only -->
      <div class="bg-overlay" :style="{ opacity: overlayOpacity }"></div>
    </div>

    <div class="max-w-7xl mx-auto flex flex-col relative z-10">
      <nav
        ref="topNav"
        class="max-w-7xl px-5 md:fixed top-0 z-[98] w-screen transition-all duration-300"
        :class="{
          'bg-transparent backdrop-blur-0': isPageLoading,
          'bg-[#121212] bg-opacity-80 backdrop-blur-md': !hasAnyBg,
          'bg-transparent backdrop-blur-0': hasAnyBg
        }"
      >
        <div class="container mx-auto flex flex-wrap items-center justify-between">
          <button @click="redirectToHome" class="flex animate-slide-down">
            <span class="self-center text-lg text-[#f6d365] font-semibold whitespace-nowrap hover:text-[#ffd27a] transition-colors">Genrald's</span>
          </button>
          <div class="flex md:order-2 animate-slide-down delay-100 space-x-2">
            <a href="https://github.com/AjieDev" class="hover:scale-110 transition-transform duration-200 inline-block">
              <img class="w-9 rounded-full" src="https://ajie.is-a.dev/cdn/img/github.png" alt="Github" />
            </a>
            <a href="https://dsc.gg/ajidevserver" class="hover:scale-110 transition-transform duration-200 inline-block">
              <img class="w-9 rounded-full" src="https://ajie.is-a.dev/cdn/img/discord.png" alt="Discord Server" />
            </a>
          </div>

          <div class="hidden md:flex justify-between items-center w-full md:w-auto md:order-1">
            <ul class="flex-col md:flex-row flex md:space-x-8 mt-4 md:mt-0 md:text-sm md:font-medium">
              <li class="animate-fade-in">
                <router-link to="/" class="nav-link">Home</router-link>
              </li>
              <li class="animate-fade-in delay-100">
                <router-link to="/about" class="nav-link">About</router-link>
              </li>
              <li class="animate-fade-in delay-200">
                <router-link to="/blog" class="nav-link">Blogs</router-link>
              </li>
              <li class="animate-fade-in delay-300">
                <router-link to="/public-lavalink" class="nav-link">Free Public Lavalink</router-link>
              </li>
            </ul>
          </div>
        </div>
      </nav>

      <div class="md:mt-[100px] page-shell">
        <router-view v-slot="{ Component }">
          <transition name="page" mode="out-in">
            <div
              class="page-wrap"
              :key="$route.fullPath"
              ref="pageContainer"
            >
              <component :is="Component" />
            </div>
          </transition>
        </router-view>
      </div>
    </div>

    <footer
      ref="bottomNav"
      class="block md:hidden fixed bottom-0 left-0 right-0 rounded-t-3xl border border-[#383838] backdrop-blur-md backdrop-opacity-90 z-20 transition-all duration-300 animate-slide-up"
      :class="{
        'bg-[#121212] bg-opacity-80': !hasAnyBg,
        'bg-transparent backdrop-blur-0': hasAnyBg
      }"
    >
      <nav class="flex justify-around py-4 text-xs">
        <router-link to="/" class="nav-link-mobile">Home</router-link>
        <router-link to="/about" class="nav-link-mobile">About</router-link>
        <router-link to="/blog" class="nav-link-mobile">Blogs</router-link>
        <router-link to="/public-lavalink" class="nav-link-mobile">Free Public Lavalink</router-link>
      </nav>
    </footer>

    <span class="relative z-10 block text-center py-8 text-gray-500 text-sm animate-pulse-slow">AjieDev Software</span>
  </div>

  <transition name="loader">
    <div
      v-if="isPageLoading"
      class="page-loader-screen"
      :style="{
        top: '0px',
        bottom: footerHeight + 'px',
        background: forceLoaderCover || ($route.meta && $route.meta.bgImage) ? '#121212' : 'transparent'
      }"
      aria-live="polite"
      aria-busy="true"
    >
      <div class="page-loader-screen__spinner"></div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'AppLayout',
  data() {
    return {
      currentBg: '',
      currentVisible: false,
      nextBg: '',
      nextVisible: false,
      placeholderHidden: false,
      isTransitioning: false,
      pendingFirstFade: false,
      pendingFirstFadeId: 0,
      bgChangeId: 0,
      fadeTimer: null,
      isPageLoading: false,
      pageLoadId: 0,
      pageLoadTimer: null,
      pageLoadDelayTimer: null,
      pageLoadStart: 0,
      loaderFadeMs: 450,
      minLoaderMs: 150,
      forceLoaderCover: true,
      firstLoadDone: false,
      navHeight: 0,
      footerHeight: 0,
      FADE_MS: 1200, // keep as data so JS duration is explicit
    };
  },
  computed: {
    hasAnyBg() {
      const meta = this.$route.meta || {};
      const metaImage = !!meta.bgImage;
      // include transitioning so UI doesn't flip mid-fade
      return !!(
        this.isTransitioning ||
        (this.currentBg && this.currentVisible) ||
        (this.nextBg && this.nextVisible) ||
        metaImage
      );
    },
    // overlayOpacity only — overlay color is fixed in CSS so no intermediate color blending
    overlayOpacity() {
      const meta = this.$route.meta || {};
      const visibleOpacity = typeof meta.bgOpacity === 'number' ? meta.bgOpacity : 0.45;
      const noBgOpacity = 0.8;
      return this.hasAnyBg ? visibleOpacity : noBgOpacity;
    },
    placeholderStyle() {
      const meta = this.$route.meta || {};
      const ph = meta.bgPlaceholder || '';
      if (!ph) return { background: '#121212' };
      return {
        backgroundImage: `url('${ph}')`,
        backgroundSize: meta.bgSize || 'cover',
        backgroundPosition: meta.bgPosition || 'center center',
        filter: 'blur(8px)',
        transform: 'scale(1.02)',
      };
    },
  },
  watch: {
    '$route.meta.bgImage': {
      immediate: true,
      handler(newUrl) {
        this.startBackgroundChange(newUrl || '');
      }
    },
    '$route.fullPath': {
      immediate: true,
      handler() {
        this.beginPageLoad();
      }
    }
  },
  methods: {
    redirectToHome() {
      this.$router.push('/');
    },

    startBackgroundChange(newUrl) {
      const FADE_MS = this.FADE_MS;
      this.bgChangeId += 1;
      const changeId = this.bgChangeId;

      if (this.fadeTimer) {
        clearTimeout(this.fadeTimer);
        this.fadeTimer = null;
      }

      const isFirstLoad = !this.currentBg && !this.nextBg;

      // fade to no-bg
      if (!newUrl) {
        this.nextBg = '';
        this.nextVisible = false;

        // hold UI as "has bg" during transition
        this.isTransitioning = true;

        // start visual fade-out
        this.currentVisible = false;

        // after fade completes, clear and stop transitioning
        this.fadeTimer = setTimeout(() => {
          if (this.bgChangeId !== changeId) return;
          this.currentBg = '';
          this.isTransitioning = false;
          this.fadeTimer = null;
        }, FADE_MS);
        return;
      }

      // if same image, keep visible
      if (newUrl === this.currentBg) {
        this.placeholderHidden = true;
        this.currentVisible = true;
        return;
      }

      // first load: render DOM image and fade in on its real load event
      if (isFirstLoad) {
        this.placeholderHidden = false;
        this.pendingFirstFade = true;
        this.pendingFirstFadeId = changeId;
        this.currentBg = newUrl;
        this.currentVisible = false;
        this.nextBg = '';
        this.nextVisible = false;
        return;
      }

      // preload new image
      this.placeholderHidden = false;
      this.nextBg = newUrl;
      this.nextVisible = false;

      const img = new Image();
      img.decoding = 'async';
      img.src = newUrl;
      img.onload = () => {
        if (this.bgChangeId !== changeId) return;
        // hold UI during crossfade
        this.isTransitioning = true;

        // keep current visible while next fades in
        this.currentVisible = true;
        this.nextVisible = true;
        this.placeholderHidden = true;

        this.fadeTimer = setTimeout(() => {
          if (this.bgChangeId !== changeId) return;
          // finish transition
          this.currentBg = this.nextBg;
          this.currentVisible = true;
          this.nextBg = '';
          this.nextVisible = false;
          this.isTransitioning = false;
          this.fadeTimer = null;
        }, FADE_MS);
      };

      img.onerror = () => {
        if (this.bgChangeId !== changeId) return;
        console.warn('Background image failed to load:', newUrl);
        this.nextBg = '';
        this.nextVisible = false;
        this.placeholderHidden = false;
      };
    },

    onCurrentImgLoad(event) {
      if (!this.pendingFirstFade) return;
      if (this.pendingFirstFadeId !== this.bgChangeId) return;
      this.pendingFirstFade = false;
      this.isTransitioning = true;

      const el = event && event.target ? event.target : null;
      const decodePromise = el && typeof el.decode === 'function'
        ? el.decode().catch(() => {})
        : Promise.resolve();

      decodePromise.then(() => {
        this.$nextTick(() => {
          requestAnimationFrame(() => {
            this.placeholderHidden = true;
            this.currentVisible = true;

            this.fadeTimer = setTimeout(() => {
              if (this.pendingFirstFadeId !== this.bgChangeId) return;
              this.isTransitioning = false;
              this.fadeTimer = null;
            }, this.FADE_MS);
          });
        });
      });
    },

    onCurrentImgError() {
      if (!this.pendingFirstFade) return;
      this.pendingFirstFade = false;
      this.currentBg = '';
      this.currentVisible = false;
      this.placeholderHidden = false;
      this.isTransitioning = false;
    },

    beginPageLoad() {
      this.pageLoadId += 1;
      const loadId = this.pageLoadId;

      if (this.pageLoadTimer) {
        clearTimeout(this.pageLoadTimer);
        this.pageLoadTimer = null;
      }
      if (this.pageLoadDelayTimer) {
        clearTimeout(this.pageLoadDelayTimer);
        this.pageLoadDelayTimer = null;
      }

      this.pageLoadStart = Date.now();
      this.isPageLoading = true;
      if (!this.firstLoadDone) this.forceLoaderCover = true;

      // hard cap to avoid hanging forever on slow/blocked assets
      this.pageLoadTimer = setTimeout(() => {
        if (this.pageLoadId !== loadId) return;
        this.finalizePageLoad(loadId, true);
      }, 2000);

      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.waitForPageImages(loadId, 0);
        });
      });

      const onReady = () => {
        if (this.pageLoadId !== loadId) return;
        if (document.readyState === 'complete') {
          this.finalizePageLoad(loadId, false);
          window.removeEventListener('load', onReady);
          document.removeEventListener('readystatechange', onReady);
        }
      };

      if (document.readyState === 'complete') {
        this.finalizePageLoad(loadId, false);
      } else {
        window.addEventListener('load', onReady);
        document.addEventListener('readystatechange', onReady);
      }

    },

    waitForPageImages(loadId, attempt) {
      if (this.pageLoadId !== loadId) return;
      const container = this.$refs.pageContainer;

      if (!container) {
        if (attempt < 6) {
          requestAnimationFrame(() => this.waitForPageImages(loadId, attempt + 1));
          return;
        }
        this.finalizePageLoad(loadId, false);
        return;
      }

      const images = Array.from(container.querySelectorAll('img'))
        .filter((img) => !img.classList.contains('bg-img') && !img.closest('.page-bg'));

      if (images.length === 0) {
        this.finalizePageLoad(loadId, false);
        return;
      }

      let remaining = 0;
      const onDone = () => {
        remaining -= 1;
        if (remaining <= 0) this.finalizePageLoad(loadId, false);
      };

      images.forEach((img) => {
        if (img.complete && img.naturalWidth > 0) return;
        remaining += 1;

        const onLoad = () => onDone();
        const onError = () => onDone();
        img.addEventListener('load', onLoad, { once: true });
        img.addEventListener('error', onError, { once: true });
      });

      if (remaining === 0) this.finalizePageLoad(loadId, false);
    },

    finalizePageLoad(loadId, force) {
      if (this.pageLoadId !== loadId) return;
      if (!force && document.readyState !== 'complete') return;
      const elapsed = Date.now() - this.pageLoadStart;
      const remaining = Math.max(0, this.minLoaderMs - elapsed) + this.loaderFadeMs;
      if (remaining > 0) {
        this.pageLoadDelayTimer = setTimeout(() => {
          if (this.pageLoadId !== loadId) return;
          this.isPageLoading = false;
          this.pageLoadDelayTimer = null;
          if (!this.firstLoadDone) {
            this.firstLoadDone = true;
            this.forceLoaderCover = false;
          }
        }, remaining);
      } else {
        this.isPageLoading = false;
        if (!this.firstLoadDone) {
          this.firstLoadDone = true;
          this.forceLoaderCover = false;
        }
      }
      if (this.pageLoadTimer) {
        clearTimeout(this.pageLoadTimer);
        this.pageLoadTimer = null;
      }
    },

    updateChromeHeights() {
      const navEl = this.$refs.topNav;
      const footerEl = this.$refs.bottomNav;
      this.navHeight = navEl ? navEl.getBoundingClientRect().height : 0;
      this.footerHeight = footerEl ? footerEl.getBoundingClientRect().height : 0;
    },
  },
  mounted() {
    this.updateChromeHeights();
    window.addEventListener('resize', this.updateChromeHeights);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateChromeHeights);
  },

  metaInfo() {
    return {
      title: "Hello! I'm Ajie Genrald!",
      meta: [
        { property: 'og:title', content: "Hello! I'm Ajie Genrald!" },
        { property: 'og:description', content: 'Novice Network engineer & Developer' },
        { property: 'og:image', content: 'https://cdn-cf.ajieblogs.eu.org/img/petv2_low.png' },
        { property: 'og:url', content: 'https://ajie.is-a.dev/' },
      ],
    };
  },
};
</script>

<style>
/* Reset + box sizing */
*,
*::before,
*::after {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

/* ensure root is dark before anything repaints */
html, body {
  height: 100%;
  background: #121212;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* scrollbars */
::-webkit-scrollbar {
  width: 5px;
  height: 5px;
}

::-webkit-scrollbar-track {
  background: hsl(240, 1%, 17%);
  border-radius: 5px;
}

::-webkit-scrollbar-thumb {
  background: #f6d365;
  border-radius: 5px;
}

/* base body */
body {
  font-family: 'Poppins', sans-serif;
  text-align: center;
  color: #2c3e50;
  min-height: 100vh;
}

/* nav spacing */
nav {
  padding: 30px;
}

/* page bg */
.page-bg {
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  z-index: 0;
  overflow: hidden;
  pointer-events: none;
  background: #121212;
}

/* rendering hints to reduce flicker */
.bg-img {
  display: block;
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  -webkit-transform: translateZ(0);
  transform: translateZ(0);
}

/* background images: consistent fade duration */
.bg-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  opacity: 0;
  transition: opacity 1200ms cubic-bezier(.2,.8,.2,1);
  will-change: opacity;
  z-index: 1;
  background: #121212; /* prevents white gaps while image is decoded/drawn */
}

.bg-img.visible {
  opacity: 1;
  z-index: 2;
}

/* placeholder matches same fade time to avoid snap */
.bg-placeholder {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  z-index: 1;
  transition: opacity 1200ms cubic-bezier(.2,.8,.2,1);
  opacity: 1;
  background: #121212;
}

.bg-placeholder.hidden {
  opacity: 0;
  pointer-events: none;
}

/* overlay: fixed color, animate opacity only to avoid color blending flashes */
.bg-overlay {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 3;
  background-color: rgba(18,18,18,1); /* fixed base color */
  transition: opacity 1200ms cubic-bezier(.2,.8,.2,1);
  opacity: 1; /* controlled via inline style binding */
}

/* nav link styles */
nav a {
  font-weight: bold;
  color: #2c3e50;
  transition: color 0.3s;
}

nav a.router-link-exact-active {
  display: inline-flex;
  flex-direction: column;
  color: white;
  transition: color 0.3s;
}

nav a.router-link-exact-active::after {
  display: inline-block;
  content: "";
  margin-top: 0.08em;
  width: 100%;
  height: 4px;
  border-radius: 2px;
  background: linear-gradient(90deg, #f6d365 0%, #f59e0b 45%, #b91c1c 100%);
}

/* Animation classes */
.fadein-bot {
  opacity: 0;
  animation: fadeInBottom 0.8s ease-out forwards;
}

.fadein-1 {
  animation-delay: 0.2s;
}

@keyframes fadeInBottom {
  from {
    opacity: 0;
    transform: translateY(0);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* New animations */
.animate-slide-down {
  animation: slideDown 0.6s ease-out forwards;
}

.animate-slide-up {
  animation: slideUp 0.6s ease-out forwards;
}

.animate-fade-in {
  opacity: 0;
  animation: fadeIn 0.6s ease-out forwards;
}

.animate-pulse-slow {
  animation: pulseSlow 3s ease-in-out infinite;
}

@keyframes slideDown {
  from { opacity: 0; transform: translateY(0); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(0); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes pulseSlow {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 0.8; }
}

.page-enter-active,
.page-leave-active {
  transition: opacity 280ms ease, transform 320ms ease;
}
.page-enter-from {
  opacity: 0;
  transform: translateY(0);
}
.page-leave-to {
  opacity: 0;
  transform: translateY(0);
}
.page-wrap {
  will-change: opacity, transform;
}
.page-loader-screen {
  position: fixed;
  inset: 0;
  display: grid;
  place-items: center;
  background: #121212;
  z-index: 1;
  pointer-events: none;
  will-change: opacity;
}
.page-loader-screen__spinner {
  width: 38px;
  height: 38px;
  border-radius: 999px;
  border: 3px solid rgba(246, 211, 101, 0.28);
  border-top-color: #f6d365;
  animation: spin 750ms linear infinite;
}
.page-loader-screen__spinner {
  transform-origin: 50% 50%;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
.loader-enter-active,
.loader-leave-active {
  transition: opacity 450ms ease;
}
.loader-enter-from,
.loader-leave-to {
  opacity: 0;
}

.delay-100 { animation-delay: 0.1s; }
.delay-200 { animation-delay: 0.2s; }
.delay-300 { animation-delay: 0.3s; }

/* Nav link styles */
.nav-link {
  display: block;
  padding: 0.5rem 0;
  color: #9ca3af;
  transition: all 0.3s ease;
  position: relative;
}

.nav-link:hover {
  color: #f59e0b;
  transform: translateY(-2px);
}

.nav-link.router-link-exact-active {
  color: white;
}

.nav-link.router-link-exact-active::after {
  display: inline-block;
  content: "";
  margin-top: 0.08em;
  width: 100%;
  height: 4px;
  border-radius: 2px;
  background: linear-gradient(90deg, #f6d365 0%, #f59e0b 45%, #b91c1c 100%);
  animation: scaleIn 0.3s ease-out;
}

@keyframes scaleIn {
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
}

/* Mobile nav link */
.nav-link-mobile {
  color: #9ca3af;
  transition: all 0.3s ease;
  padding: 0.5rem;
}

.nav-link-mobile:hover {
  color: #f59e0b;
  transform: scale(1.1);
}

.nav-link-mobile.router-link-exact-active {
  color: #f6d365;
}

/* old animation classes kept for compatibility */

/* Responsive adjustments */
@media (max-width: 768px) {
  nav {
    padding: 20px;
  }
}

/* Ensure proper stacking context */
.relative {
  position: relative;
}

.relative.z-10 {
  z-index: 10;
}

/* Footer mobile styles */
footer {
  transition: background-color 0.3s ease, backdrop-filter 0.3s ease;
}

/* Smooth transitions for navbar background */
nav {
  transition: background-color 0.3s ease, backdrop-filter 0.3s ease;
}
</style>
